import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Activity, Building2, Mail, Calendar, 
  User, AlertTriangle, AlertCircle, Loader2, 
  Target, Shield, BarChart3 
} from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import superAdminServices from '../../../../services/superAdminServices';

export default function AllActivitiesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityData, setActivityData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchActivityDetails();
    }
  }, [id]);

  const fetchActivityDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminServices.getActivityDetails(authTokens.access, id);
      setActivityData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Active': 'bg-green-100 text-green-700',
      'Inactive': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-blue-100 text-blue-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        <Activity className="h-4 w-4 mr-1" />
        {status}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probabilityColors = {
      'mineur': 'bg-blue-100 text-blue-700 border-blue-200',
      'normal': 'bg-green-100 text-green-700 border-green-200',
      'majeur': 'bg-orange-100 text-orange-700 border-orange-200',
      'critique': 'bg-red-100 text-red-700 border-red-200'
    };

    const colorClass = probabilityColors[probability] || 'bg-gray-100 text-gray-700 border-gray-200';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
        <Target className="h-3 w-3 mr-1" />
        {probability === 'mineur' ? 'Mineur' : 
         probability === 'normal' ? 'Normal' : 
         probability === 'majeur' ? 'Majeur' : 
         probability === 'critique' ? 'Critique' : probability}
      </span>
    );
  };

  const getRiskStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'approved': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
        {status === 'pending' ? 'En attente' : 
         status === 'approved' ? 'Approuvé' : 
         status === 'rejected' ? 'Rejeté' : status}
      </span>
    );
  };

  const getRiskCard = (risk) => {
    return (
      <div key={risk.id} className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 mr-2" />
              <h4 className="text-sm font-medium text-gray-900">{risk.risk_name}</h4>
            </div>
            
            {risk.description && (
              <p className="text-xs text-gray-600 mb-3">{risk.description}</p>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getProbabilityBadge(risk.probability)}
                {getRiskStatusBadge(risk.status)}
              </div>
              
              <div className="text-xs text-gray-500">
                {risk.actions_count} action(s)
              </div>
            </div>
            
            <div className="mt-2 flex items-center text-xs text-gray-500">
              <User className="h-3 w-3 mr-1" />
              <span>Créé par: {risk.created_by?.name || risk.created_by?.email || 'Inconnu'}</span>
              <span className="mx-2">•</span>
              <Calendar className="h-3 w-3 mr-1" />
              <span>{new Date(risk.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!activityData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée d'activité trouvée</p>
        </div>
      </div>
    );
  }

  const { activity, organization, created_by, last_updated_by, risks } = activityData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/activities')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{activity.activity_name}</h1>
            <p className="text-xs text-gray-500">Détails de l'activité</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(activity.status)}
        </div>
      </div>

      <div className="space-y-6">
        {/* Activity Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Activity className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations de l'activité</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom de l'activité</label>
                  <p className="text-sm text-gray-900">{activity.activity_name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{activity.description || 'Aucune description'}</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(activity.status)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(activity.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Dernière modification</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(activity.updated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Information Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        organization.is_subscribed 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Organisation créée le</label>
                    <p className="text-sm text-gray-900">
                      {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator and Updater Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Created By */}
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {created_by.full_name || `${created_by.first_name} ${created_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{created_by.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Last Updated By */}
          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {last_updated_by.full_name || `${last_updated_by.first_name} ${last_updated_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Risk Statistics Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques des risques</h2>
            </div>
            <div className="text-xs text-gray-500">
              {risks.statistics.total_risks} risque(s) total
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Status Statistics */}
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-yellow-600">{risks.statistics.risks_by_status.pending}</div>
              <div className="text-xs text-gray-600">En attente</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-green-600">{risks.statistics.risks_by_status.approved}</div>
              <div className="text-xs text-gray-600">Approuvés</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-red-600">{risks.statistics.risks_by_status.rejected}</div>
              <div className="text-xs text-gray-600">Rejetés</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-orange-600">{risks.statistics.risks_by_probability.critique}</div>
              <div className="text-xs text-gray-600">Critiques</div>
            </div>
          </div>
        </div>

        {/* Associated Risks Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-orange-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Risques associés</h2>
            </div>
            <div className="text-xs text-gray-500">
              {risks.risks_list.length} risque(s)
            </div>
          </div>

          {risks.risks_list.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {risks.risks_list.map((risk) => getRiskCard(risk))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucun risque associé à cette activité</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
